/**
 * SF-4656 — save ONE company field straight away from the detail page.
 *
 * Shared by the Feature Flags tab, the inline General Info controls and the
 * CSAT company-default radio. It is the SF-4576 save model (see
 * McpAccessSwitch), pulled out so every control on the page saves the same way:
 *  - the PATCH carries only the one field, so it can never touch anything else;
 *  - the company the PATCH returns is written into THAT company's cache entry
 *    (`onSaved`), so the new value, and any tab it shows or hides, appears at
 *    once and survives a failed refresh;
 *  - that company is then re-read; any later successful read replaces the cache.
 *
 * One save at a time per company, across every control on the page and the
 * external AI switch and the SSO setup too: admission and the "running" record
 * live in companySaveStore, outside React, so a second click, a confirmation
 * left open, a remounted control or a page you left and came back to cannot
 * start an overlapping write. Everything about a save (PATCH, cache write,
 * re-read, messages) is bound to the company it STARTED on.
 */
import { useEffect, useRef } from 'react';
import { message } from 'antd';
import type { Company } from '../list';
import { useCompanyPatch } from './useCompanyPatch';
import {
  beginCompanySave,
  endCompanySave,
  updateCompanySave,
  useRunningCompanySave,
} from './companySaveStore';
import { markCompanyReadFailed } from './companyReadFailedStore';

type SaveError = { response?: { data?: { message?: string } }; message?: string };

export interface UseSaveCompanyFieldOptions {
  companyId: string;
  /** The company's name, used in messages that arrive after staff moved on. */
  companyName?: string;
  /** Writes the company the PATCH returned into THAT company's cache entry. */
  onSaved: (saved: Company, companyId: string) => void;
  /**
   * Re-reads THAT company (not whichever one the URL shows by then).
   * Resolves `{ isError }` rather than throwing.
   */
  refetchCompany: (companyId: string) => Promise<{ isError?: boolean }>;
}

export interface PendingSave {
  field: keyof Company;
  value: unknown;
}

export interface SaveFieldOptions {
  /**
   * Sees a failed PATCH first; return true when it has told staff itself, so
   * the generic error message is not shown as well.
   */
  onError?: (err: unknown) => boolean;
  /**
   * SF-5055: the line to show for a failed PATCH instead of sf-api's message (null = use it).
   * Shown like every other save message, so it still names the company if staff moved on.
   */
  errorText?: (err: unknown) => string | null;
  /**
   * SF-5126: sends the save to the company's own action route instead of the one-field PATCH
   * (e.g. making a new forwarding address). Resolves with the company fields the server changed;
   * everything else (one save at a time, cache write, re-read, messages) is the same.
   */
  send?: (companyId: string) => Promise<Partial<Company>>;
  /**
   * SF-5127: for actions that change more of the company than their answer says (a sending-domain
   * action changes who emails come from, and its answer never carries the sender), and that can
   * change it and still fail (a check that finds the registration gone saves that, then answers
   * 502). The company is re-read after a failed save too, keeping the error message, and when
   * that re-read fails, after a success or a failure, the company is marked in
   * companyReadFailedStore until a later read of it succeeds.
   */
  rereadRequired?: boolean;
}

export const SAVE_REFUSED_TEXT = 'Another change for this company is still saving. Try again in a moment.';

export function useSaveCompanyField({
  companyId,
  companyName,
  onSaved,
  refetchCompany,
}: UseSaveCompanyFieldOptions) {
  const patchCompany = useCompanyPatch();
  const running = useRunningCompanySave(companyId);
  const pending: PendingSave | null =
    running?.kind === 'field' && running.field ? { field: running.field, value: running.value } : null;

  // Which company the page shows when a save finishes (not when it started),
  // and whether this page is still on screen at all (staff may have gone to
  // the list and on to another company: this hook then never hears of it).
  const shownCompanyRef = useRef(companyId);
  shownCompanyRef.current = companyId;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  /** Returns true when the server saved the value. */
  const save = async <K extends keyof Company>(
    field: K,
    value: Company[K],
    successText: string,
    options?: SaveFieldOptions,
  ): Promise<boolean> => {
    const forCompany = companyId;
    const forName = companyName;
    // A message that arrives after staff moved to another company says which
    // company it is about.
    const say = (text: string) =>
      mounted.current && shownCompanyRef.current === forCompany
        ? text
        : `${forName ?? 'Another company'}: ${text}`;

    const token = beginCompanySave(forCompany, { kind: 'field', field, value });
    if (token === null) {
      message.warning(SAVE_REFUSED_TEXT);
      return false;
    }
    try {
      let saved: Company;
      try {
        saved = options?.send
          ? ((await options.send(forCompany)) as Company)
          : await patchCompany(forCompany, { [field]: value } as Partial<Company>);
      } catch (err) {
        const e = err as SaveError;
        if (options?.onError?.(err) !== true) {
          message.error(
            say(
              options?.errorText?.(err) ||
                e?.response?.data?.message ||
                e?.message ||
                'Could not save the setting.',
            ),
          );
        }
        if (options?.rereadRequired) {
          const refreshed = await refetchCompany(forCompany).catch(() => ({ isError: true }));
          if (refreshed?.isError) markCompanyReadFailed(forCompany);
        }
        // The control falls back to the saved value: unchanged on any error.
        return false;
      }
      // Keep what the server confirmed; fall back to what we sent if the
      // response leaves the field out.
      const confirmed = saved?.[field] !== undefined ? saved[field] : value;
      onSaved({ ...saved, [field]: confirmed }, forCompany);
      updateCompanySave(forCompany, token, confirmed);
      message.success(say(successText));
      const refreshed = await refetchCompany(forCompany).catch(() => ({ isError: true }));
      if (refreshed?.isError) {
        if (options?.rereadRequired) markCompanyReadFailed(forCompany);
        message.warning(say('Saved, but the page could not refresh'));
      }
      return true;
    } finally {
      endCompanySave(forCompany, token);
    }
  };

  return { save, pending, busy: running !== null };
}
